import React, { useEffect, useMemo, useRef, useState } from "react";

import { getOwnerLine } from "../utils/ownerLine";

const STORAGE_LAYOUT_KEY = "monity.live.layout.v2";
const STORAGE_SLOTS_KEY = "monity.live.slots.v2";
const STORAGE_AUTO_LAYOUT_KEY = "monity.live.autoLayout.v1";
const LIVE_SOURCE_POLL_MS = 10000;
const WATCH_PING_MS = 8000;
const SOURCE_PAGE_SIZE = 8;

const LAYOUT_OPTIONS = [
  { key: "single", label: "1", count: 1 },
  { key: "quad", label: "2×2", count: 4 },
  { key: "nine", label: "3×3", count: 9 },
  { key: "sixteen", label: "4×4", count: 16 },
];

const MAX_SLOTS = 16;
const EMPTY_SLOTS = Array.from({ length: MAX_SLOTS }, () => "");

function getLayoutCount(layoutKey) {
  return LAYOUT_OPTIONS.find((item) => item.key === layoutKey)?.count || 4;
}

// 송출 중인 대수에 맞는 화면 분할을 고른다(2026-10-07).
//   1대면 1화면, 2~4대면 2×2 … 딱 맞는 가장 작은 분할을 쓴다.
//   0대일 때는 바꾸지 않는다(켜질 때 화면이 튀지 않게).
function pickLayoutForCount(count) {
  const n = Number(count || 0);
  if (n <= 0) return null;

  const found = LAYOUT_OPTIONS.find((item) => item.count >= n);
  return found ? found.key : LAYOUT_OPTIONS[LAYOUT_OPTIONS.length - 1].key;
}

function getSourceTypeLabel(type) {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "mintcam") return "민트캠";
  if (normalized === "mobile_cctv") return "이동형 CCTV";
  if (normalized === "nvr_live") return "NVR LIVE";
  return "LIVE";
}

function getStatusBadge(source) {
  const status = String(source?.liveStatus || "").toUpperCase();

  if (!source?.isActive || status === "DISABLED") {
    return { text: "비활성", cls: "bg-gray-500" };
  }

  if (status === "ONLINE") {
    return { text: "ONLINE", cls: "bg-green-600" };
  }

  return { text: "OFFLINE", cls: "bg-red-600" };
}

function normalizeIncomingSource(item, index = 0) {
  const sourceId =
    String(
      item?.sourceId ||
        item?.id ||
        item?.camIdx ||
        item?.camNo ||
        `source-${index + 1}`
    ).trim() || `source-${index + 1}`;

  const streamKey = String(item?.streamKey || "").trim();
  const streamPath = String(item?.streamPath || "").trim();

  return {
    sourceId,
    id: sourceId,
    camIdx: Number(item?.camIdx || 0),
    camNo: Number(item?.camNo || 0),
    name: item?.name || item?.camName || item?.camTitle || `소스 ${index + 1}`,
    sourceType: String(item?.sourceType || item?.type || "mintcam")
      .trim()
      .toLowerCase(),
    siteName: String(item?.siteName || item?.site || "").trim(),
    ownerLoginId: String(item?.ownerLoginId || "").trim(),
    ownerUserName: String(item?.ownerUserName || "").trim(),
    // 작업자 이름과 항상 같이 표시되는 값들(지점/팀/차량번호)
    ownerBranchName: String(item?.ownerBranchName || "").trim(),
    ownerTeamName: String(item?.ownerTeamName || "").trim(),
    ownerVehicleNo: String(item?.ownerVehicleNo || "").trim(),
    streamKey,
    streamPath,
    webRtcUrl: String(item?.webRtcUrl || "").trim(),
    rtspUrl: String(item?.rtspUrl || "").trim(),
    sourceRtsp: String(item?.sourceRtsp || "").trim(),
    rtmpPublishUrl: String(item?.rtmpPublishUrl || "").trim(),
    isActive: item?.isActive === undefined ? true : Boolean(item?.isActive),
    liveStatus: String(item?.liveStatus || "OFFLINE").trim().toUpperCase(),
    battery:
      item?.battery === null || item?.battery === undefined
        ? null
        : Number(item?.battery),
    updatedAt: item?.updatedAt || "",
    lastHeartbeatAt: item?.lastHeartbeatAt || "",
    createdAt: item?.createdAt || "",
  };
}

function getOrCreateViewerTabId() {
  try {
    const key = "monity.live.viewer.tabId";
    let value = sessionStorage.getItem(key);
    if (value) return value;

    value = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(key, value);
    return value;
  } catch {
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function createUniqueWatchId(prefix = "watch") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function postWatch(url, body) {
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (err) {
    console.error(`watch api 호출 실패: ${url}`, err);
  }
}

function CameraPlayer({ source }) {
  const tabIdRef = useRef(getOrCreateViewerTabId());
  const watchIdRef = useRef(createUniqueWatchId("live-player"));
  const pingTimerRef = useRef(null);
  const startedRef = useRef(false);
  const [playerLoading, setPlayerLoading] = useState(false);

  useEffect(() => {
    const clearPing = () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };

    const stopWatching = async () => {
      clearPing();

      if (!startedRef.current || !source?.streamKey) {
        startedRef.current = false;
        return;
      }

      startedRef.current = false;

      await postWatch("/api/live/watch/stop", {
        streamKey: source.streamKey,
        tabId: tabIdRef.current,
        watchId: watchIdRef.current,
      });
    };

    const startWatching = async () => {
      clearPing();

      if (!source || !source.isActive || !source.streamKey || !source.webRtcUrl) {
        startedRef.current = false;
        return;
      }

      await postWatch("/api/live/watch/start", {
        streamKey: source.streamKey,
        tabId: tabIdRef.current,
        watchId: watchIdRef.current,
      });

      startedRef.current = true;

      pingTimerRef.current = setInterval(() => {
        postWatch("/api/live/watch/ping", {
          streamKey: source.streamKey,
          tabId: tabIdRef.current,
          watchId: watchIdRef.current,
        });
      }, WATCH_PING_MS);
    };

    startWatching();

    return () => {
      stopWatching();
    };
  }, [source?.streamKey, source?.webRtcUrl, source?.isActive]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!startedRef.current || !source?.streamKey) return;

      navigator.sendBeacon?.(
        "/api/live/watch/stop",
        new Blob(
          [
            JSON.stringify({
              streamKey: source.streamKey,
              tabId: tabIdRef.current,
              watchId: watchIdRef.current,
            }),
          ],
          { type: "application/json" }
        )
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [source?.streamKey]);

  useEffect(() => {
    if (
      !source ||
      !source.isActive ||
      String(source.liveStatus || "").toUpperCase() !== "ONLINE" ||
      !source.webRtcUrl
    ) {
      setPlayerLoading(false);
      return;
    }

    setPlayerLoading(true);
    // 오버레이는 반투명이라 영상이 먼저 나와도 가려 보이므로 짧게만 표시한다.
    const timer = setTimeout(() => {
      setPlayerLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [source?.sourceId, source?.webRtcUrl, source?.isActive, source?.liveStatus]);

  if (!source) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm text-gray-300">
        카메라를 선택하면 실시간 영상이 표시됩니다.
      </div>
    );
  }

  if (!source.isActive) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm text-red-300">
        비활성화된 소스입니다.
      </div>
    );
  }

  if (!source.webRtcUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm text-gray-300">
        재생 URL이 없습니다.
      </div>
    );
  }

  if (String(source.liveStatus || "").toUpperCase() !== "ONLINE") {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center">
        <div className="text-sm font-semibold text-white">
          현재 오프라인 상태입니다.
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <iframe
        key={source.webRtcUrl}
        title={source.name || source.sourceId}
        src={source.webRtcUrl}
        className="absolute inset-0 block h-full w-full"
        style={{ border: 0, background: "black" }}
        allow="autoplay; fullscreen; encrypted-media"
      />

      {playerLoading && (
        <div className="pointer-events-none absolute inset-0 z-[1] flex flex-col items-center justify-center bg-black/55 px-4 text-center">
          <div className="text-sm font-semibold text-white">
            실시간 영상을 연결 중입니다.
          </div>
          <div className="mt-2 text-[11px] text-white/65">
            잠시 후 영상이 표시됩니다.
          </div>
        </div>
      )}

      <div className="absolute bottom-2 right-2 z-[2] flex gap-2">
        <button
          onClick={() =>
            window.open(source.webRtcUrl, "_blank", "noopener,noreferrer")
          }
          className="rounded-lg bg-white/90 px-3 py-1.5 text-[11px] font-bold text-black hover:bg-white"
        >
          새창
        </button>
      </div>
    </div>
  );
}

function getGridBoxHeight(layout) {
  if (layout === "single") return "76vh";
  if (layout === "quad") return "76vh";
  return "76vh";
}

function getSlotMinHeight(layout) {
  if (layout === "single") return "76vh";
  if (layout === "quad") return "36vh";
  if (layout === "sixteen") return "18vh";
  return "23vh";
}

export default function LiveView({ cameras, loading, onRefresh }) {
  const [layout, setLayout] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_LAYOUT_KEY) || "quad";
    } catch {
      return "quad";
    }
  });

  // 배치된 카메라 전체 목록(가변 길이). 그리드는 이 목록을 페이지 단위로 보여준다.
  const [slotAssignments, setSlotAssignments] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_SLOTS_KEY) || "[]");
      if (!Array.isArray(raw)) return [];
      return raw.map((v) => String(v || ""));
    } catch {
      return [];
    }
  });

  // «자동 분할» — 송출 중인 대수에 맞춰 1 / 2×2 / 3×3 / 4×4 를 알아서 고른다(기본 켬).
  //   분할 버튼을 직접 누르면 꺼진다(사용자가 고른 화면을 되돌리지 않기 위해).
  const [autoLayout, setAutoLayout] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_AUTO_LAYOUT_KEY);
      return saved === null ? true : saved === "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_AUTO_LAYOUT_KEY, autoLayout ? "1" : "0");
    } catch {}
  }, [autoLayout]);

  // 그리드 페이지 (0-based)
  const [gridPage, setGridPage] = useState(0);

  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [sourceMsg, setSourceMsg] = useState("");
  const [sourcePage, setSourcePage] = useState(1);
  const [sourceSearch, setSourceSearch] = useState("");

  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const initializedAutoFillRef = useRef(false);

  const fallbackSources = useMemo(() => {
    const raw = Array.isArray(cameras) ? cameras : [];
    return raw.map((item, index) =>
      normalizeIncomingSource(
        {
          ...item,
          sourceId: `mintcam-${item?.camIdx ?? item?.camNo ?? index + 1}`,
          sourceType: item?.sourceType || "mintcam",
          liveStatus: item?.liveStatus || "OFFLINE",
        },
        index
      )
    );
  }, [cameras]);

  const effectiveSources = useMemo(() => {
    if (sources.length > 0) return sources;
    return fallbackSources;
  }, [sources, fallbackSources]);

  // 검색어로 소스 필터링 (CAM 번호 / 이름 / 담당자). 기존 페이지 방식은 유지.
  const filteredSources = useMemo(() => {
    const q = String(sourceSearch || "").trim().toLowerCase();
    if (!q) return effectiveSources;
    return effectiveSources.filter((item) => {
      const camNo = String(item?.camNo ?? "").toLowerCase();
      const name = String(item?.name ?? "").toLowerCase();
      const owner = String(item?.ownerUserName ?? "").toLowerCase();
      return (
        camNo.includes(q) ||
        ("cam " + camNo).includes(q) ||
        name.includes(q) ||
        owner.includes(q)
      );
    });
  }, [effectiveSources, sourceSearch]);

  const sourcePageCount = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSources.length / SOURCE_PAGE_SIZE));
  }, [filteredSources.length]);

  const pagedSources = useMemo(() => {
    const safePage = Math.min(Math.max(sourcePage, 1), sourcePageCount);
    const start = (safePage - 1) * SOURCE_PAGE_SIZE;
    return filteredSources.slice(start, start + SOURCE_PAGE_SIZE);
  }, [filteredSources, sourcePage, sourcePageCount]);

  // 검색어 바뀌면 1페이지로
  useEffect(() => {
    setSourcePage(1);
  }, [sourceSearch]);

  const visibleSlotCount = getLayoutCount(layout);

  const sourceMap = useMemo(() => {
    return effectiveSources.reduce((acc, item) => {
      acc[item.sourceId] = item;
      return acc;
    }, {});
  }, [effectiveSources]);

  // 실제로 화면에 깔 배치.
  //   «송출중만» 이 켜져 있으면(기본) 저장된 슬롯 배치를 쓰지 않고 송출 중인 카메라만 순서대로 깐다.
  //   꺼져 있는 카메라가 칸을 차지해 빈 화면만 늘어나는 걸 막기 위한 것(2026-10-07).
  //   저장된 배치는 건드리지 않으므로, 토글을 끄면 예전처럼 수동 배치가 그대로 돌아온다.
  const onlineSourceIds = useMemo(() => {
    return effectiveSources
      .filter(
        (item) => String(item?.liveStatus || "").toUpperCase() === "ONLINE"
      )
      .map((item) => item.sourceId);
  }, [effectiveSources]);

  // 송출 중인 대수가 바뀌면 화면 분할을 거기 맞춘다(자동 분할이 켜져 있을 때만).
  const onlineCount = onlineSourceIds.length;

  useEffect(() => {
    if (!autoLayout) return;

    const next = pickLayoutForCount(onlineCount);
    if (!next || next === layout) return;

    setLayout(next);
    setGridPage(0);
  }, [autoLayout, onlineCount, layout]);

  /*
    화면 배치 — «송출 중»을 앞으로, 꺼진 카메라도 뒤에 같이 깐다.

    2026-10-07 에 한동안 송출 중인 것만 깔았는데, 전부 꺼져 있으면 분할 버튼을 눌러도
    화면에 아무것도 안 나와서 «고장난 것»처럼 보였다. 관제 화면은 지금 몇 대가 켜져 있고
    몇 대가 꺼져 있는지도 보는 자리다. 그래서 꺼진 카메라는 «오프라인» 칸으로 보여준다.

    대신 순서를 고정한다 — 켜진 것 먼저, 그 다음 지정해 둔 슬롯 순서, 나머지는 원래 순서.
    그래야 켜진 카메라가 항상 첫 페이지 앞쪽에 모여 스크롤·페이지 넘김이 필요 없다.
  */
  const activeAssignments = useMemo(() => {
    const slotRank = new Map();
    slotAssignments.forEach((id, idx) => {
      const key = String(id || "").trim();
      if (key && !slotRank.has(key)) slotRank.set(key, idx);
    });

    const onlineSet = new Set(onlineSourceIds);
    const order = new Map();
    effectiveSources.forEach((item, idx) => order.set(item.sourceId, idx));

    return effectiveSources
      .map((item) => item.sourceId)
      .sort((a, b) => {
        // 1) 송출 중 먼저
        const oa = onlineSet.has(a) ? 0 : 1;
        const ob = onlineSet.has(b) ? 0 : 1;
        if (oa !== ob) return oa - ob;

        // 2) 지정해 둔 슬롯 순서
        const ra = slotRank.has(a) ? slotRank.get(a) : Infinity;
        const rb = slotRank.has(b) ? slotRank.get(b) : Infinity;
        if (ra !== rb) return ra - rb;

        // 3) 원래 목록 순서
        return (order.get(a) ?? 0) - (order.get(b) ?? 0);
      });
  }, [slotAssignments, onlineSourceIds, effectiveSources]);

  // 배치된 카메라 수 기준 그리드 총 페이지 수
  const gridPageCount = useMemo(() => {
    const placed = activeAssignments.filter(Boolean).length;
    return Math.max(1, Math.ceil(placed / visibleSlotCount));
  }, [activeAssignments, visibleSlotCount]);

  // 현재 페이지에 해당하는 슬롯들 (이 페이지의 영상만 실제 연결됨)
  const visibleSlots = useMemo(() => {
    const base = gridPage * visibleSlotCount;
    return Array.from({ length: visibleSlotCount }, (_, i) => {
      const absIndex = base + i;
      const sourceId = activeAssignments[absIndex] || "";
      return {
        slotIndex: absIndex,
        channelNo: absIndex + 1,
        sourceId,
        source: sourceMap[sourceId] || null,
      };
    });
  }, [gridPage, visibleSlotCount, activeAssignments, sourceMap]);

  // 페이지/레이아웃이 바뀌어 현재 페이지가 범위를 벗어나면 보정
  useEffect(() => {
    if (gridPage > gridPageCount - 1) {
      setGridPage(Math.max(0, gridPageCount - 1));
    }
  }, [gridPage, gridPageCount]);

  const loadLiveSources = async ({ silent = false } = {}) => {
    if (!silent) {
      setSourcesLoading(true);
    }

    try {
      const res = await fetch("/api/live-sources", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok || !data?.success || !Array.isArray(data?.sources)) {
        setSources([]);
        if (!silent) {
          setSourceMsg(data?.message || "실시간 소스 조회 실패");
        }
        return;
      }

      const normalized = data.sources.map((item, index) =>
        normalizeIncomingSource(item, index)
      );

      setSources(normalized);
      if (!silent) {
        setSourceMsg("");
      }
    } catch (err) {
      console.error("❌ /api/live-sources error:", err);
      setSources([]);
      if (!silent) {
        setSourceMsg("실시간 소스 조회 중 오류가 발생했습니다.");
      }
    } finally {
      if (!silent) {
        setSourcesLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const clearPollingTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = (delayMs = LIVE_SOURCE_POLL_MS) => {
      clearPollingTimer();
      if (!mountedRef.current) return;
      if (document.hidden) return;

      timerRef.current = setTimeout(async () => {
        await loadLiveSources({ silent: true });
        scheduleNext(delayMs);
      }, delayMs);
    };

    const init = async () => {
      await loadLiveSources();
      scheduleNext();
    };

    const handleVisibilityChange = async () => {
      if (!mountedRef.current) return;

      if (document.hidden) {
        clearPollingTimer();
        return;
      }

      await loadLiveSources({ silent: true });
      scheduleNext();
    };

    init();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearPollingTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_LAYOUT_KEY, layout);
    } catch {}
  }, [layout]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_SLOTS_KEY, JSON.stringify(slotAssignments));
    } catch {}
  }, [slotAssignments]);

  useEffect(() => {
    if (sourcePage > sourcePageCount) {
      setSourcePage(sourcePageCount);
    }

    if (sourcePage < 1) {
      setSourcePage(1);
    }
  }, [sourcePage, sourcePageCount]);

  useEffect(() => {
    const validIds = new Set(effectiveSources.map((item) => item.sourceId));

    setSlotAssignments((prev) => {
      // 더 이상 존재하지 않는 소스는 제거(빈칸으로). 길이/순서는 유지.
      const next = prev.map((sourceId) =>
        validIds.has(String(sourceId)) ? sourceId : ""
      );
      const changed = next.some((value, idx) => value !== prev[idx]);
      return changed ? next : prev;
    });

    if (selectedSourceId && !validIds.has(selectedSourceId)) {
      setSelectedSourceId("");
    }
  }, [effectiveSources, selectedSourceId]);

  useEffect(() => {
    if (initializedAutoFillRef.current) return;
    if (effectiveSources.length === 0) return;

    const hasAssigned = slotAssignments.some(Boolean);
    if (!hasAssigned) {
      // 최초 진입 시 모든 소스를 순서대로 배치 (그리드 페이지로 넘겨 본다)
      setSlotAssignments(effectiveSources.map((item) => item.sourceId));
    }

    initializedAutoFillRef.current = true;
  }, [effectiveSources, slotAssignments]);

  // 슬롯 배치는 «순서 지정» 용도다(화면에는 송출 중인 것만 깔린다).
  //   지정해 둔 카메라가 켜지면 앞 칸으로 온다.
  const assignSourceToSlot = (slotIndex, sourceId) => {
    if (!sourceId) return;

    setSlotAssignments((prev) => {
      const next = [...prev];
      while (next.length <= slotIndex) next.push("");
      next[slotIndex] = sourceId;
      return next;
    });
  };

  const clearSlot = (slotIndex) => {
    setSlotAssignments((prev) => {
      const next = [...prev];
      if (slotIndex < next.length) next[slotIndex] = "";
      return next;
    });
  };

  // 더블클릭: 배치 목록의 첫 빈 자리(없으면 맨 끝)에 추가. 이미 있으면 무시.
  const assignToNextEmptySlot = (sourceId) => {
    if (!sourceId) return;

    setSlotAssignments((prev) => {
      if (prev.includes(sourceId)) return prev;
      const emptyIdx = prev.findIndex((val) => !val);
      const next = [...prev];
      if (emptyIdx === -1) {
        next.push(sourceId);
      } else {
        next[emptyIdx] = sourceId;
      }
      return next;
    });
  };

  // 자동 배치: 모든 소스를 순서대로 배치 (그리드를 이전/다음으로 넘겨 본다)
  const autoFillSlots = () => {
    setSlotAssignments(effectiveSources.map((item) => item.sourceId));
    setGridPage(0);
  };

  const clearAllSlots = () => {
    setSlotAssignments([]);
    setGridPage(0);
  };

  const handleSlotClick = (slotIndex) => {
    if (!selectedSourceId) return;
    assignSourceToSlot(slotIndex, selectedSourceId);
  };

  const handleDropOnSlot = (e, slotIndex) => {
    e.preventDefault();
    const draggedSourceId = String(
      e.dataTransfer.getData("text/plain") || ""
    ).trim();

    if (!draggedSourceId) return;
    assignSourceToSlot(slotIndex, draggedSourceId);
  };

  const handleRefreshClick = async () => {
    try {
      setManualRefreshing(true);
      await Promise.all([loadLiveSources(), Promise.resolve(onRefresh?.())]);
    } catch {
    } finally {
      setManualRefreshing(false);
    }
  };

  const goPrevSourcePage = () => {
    setSourcePage((prev) => Math.max(1, prev - 1));
  };

  const goNextSourcePage = () => {
    setSourcePage((prev) => Math.min(sourcePageCount, prev + 1));
  };

  const gridColsClass =
    layout === "single"
      ? "grid-cols-1"
      : layout === "quad"
      ? "grid-cols-2"
      : layout === "sixteen"
      ? "grid-cols-4"
      : "grid-cols-3";

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12 xl:gap-5">
      <div className="min-w-0 xl:col-span-3">
        <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-md dark:bg-[#1F2A3B]">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="text-sm font-bold">실시간 소스 목록</div>
            <button
              onClick={handleRefreshClick}
              className="rounded-lg bg-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-100"
            >
              {manualRefreshing ? "갱신 중..." : "새로고침"}
            </button>
          </div>

          <div className="mb-2 flex flex-wrap items-center gap-2">
            {LAYOUT_OPTIONS.map((item) => {
              const active = layout === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => {
                    // 직접 고르면 자동 분할을 끈다 — 고른 화면이 되돌려지지 않게.
                    setAutoLayout(false);
                    setLayout(item.key);
                    setGridPage(0);
                  }}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                    active
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white dark:hover:bg-[#162033]"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}

            {/* 자동 분할 — 송출 중인 대수에 맞는 화면 분할을 알아서 고른다. */}
            <button
              type="button"
              onClick={() => setAutoLayout((prev) => !prev)}
              aria-pressed={autoLayout}
              title={
                autoLayout
                  ? `자동 분할 켜짐 — 송출 중 ${onlineCount}대에 맞춰 선택됩니다`
                  : "자동 분할 꺼짐 — 직접 고른 분할을 유지합니다"
              }
              className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                autoLayout
                  ? "border-emerald-600 bg-emerald-600 text-white"
                  : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white dark:hover:bg-[#162033]"
              }`}
            >
              자동
            </button>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={autoFillSlots}
              className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300"
            >
              자동 배치
            </button>
            <button
              onClick={clearAllSlots}
              className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
            >
              전체 비우기
            </button>
          </div>

          <div className="mb-3">
            <input
              type="text"
              value={sourceSearch}
              onChange={(e) => setSourceSearch(e.target.value)}
              placeholder="CAM 번호 / 이름 검색"
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
            />
            <div className="mt-1 text-[11px] text-gray-400">
              더블클릭하면 빈 슬롯에 바로 들어갑니다.
            </div>
          </div>

          {sourceMsg ? (
            <div className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
              {sourceMsg}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {(filteredSources || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                {sourcesLoading || loading
                  ? "실시간 소스를 불러오는 중..."
                  : sourceSearch
                  ? "검색 결과가 없습니다."
                  : "표시할 실시간 소스가 없습니다."}
              </div>
            ) : (
              pagedSources.map((source) => {
                const badge = getStatusBadge(source);
                const selected = selectedSourceId === source.sourceId;

                return (
                  <div
                    key={source.sourceId}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", source.sourceId);
                      setSelectedSourceId(source.sourceId);
                    }}
                    onClick={() => setSelectedSourceId(source.sourceId)}
                    onDoubleClick={() => assignToNextEmptySlot(source.sourceId)}
                    title="더블클릭: 빈 슬롯에 바로 추가"
                    className={`cursor-pointer rounded-2xl border p-3 transition ${
                      selected
                        ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                        : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-[#0F172A] dark:hover:bg-[#162033]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-gray-900 dark:text-white">
                          {source.name}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          {getOwnerLine(source) ||
                            `CAM ${source.camNo || "-"} / ${getSourceTypeLabel(
                              source.sourceType
                            )}`}
                        </div>
                      </div>

                      <span
                        className={`rounded-full px-2 py-1 text-[10px] font-bold text-white ${badge.cls}`}
                      >
                        {badge.text}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {(filteredSources || []).length > SOURCE_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
              <button
                type="button"
                onClick={goPrevSourcePage}
                disabled={sourcePage <= 1}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
              >
                ◀ 이전
              </button>

              <div className="min-w-[72px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                {sourcePage} / {sourcePageCount}
              </div>

              <button
                type="button"
                onClick={goNextSourcePage}
                disabled={sourcePage >= sourcePageCount}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
              >
                다음 ▶
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="min-w-0 xl:col-span-9">
        <div className="flex h-full flex-col rounded-2xl bg-white p-4 shadow-md dark:bg-[#1F2A3B]">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-lg font-bold">실시간 관제</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                왼쪽 목록을 더블클릭·드래그해서 배치하세요.
              </div>
            </div>

            {/* 페이지 넘김 (슬롯보다 카메라가 많을 때) */}
            {gridPageCount > 1 ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setGridPage((p) => Math.max(0, p - 1))}
                  disabled={gridPage <= 0}
                  className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
                >
                  ◀ 이전
                </button>
                <div className="min-w-[72px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                  {gridPage + 1} / {gridPageCount}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setGridPage((p) => Math.min(gridPageCount - 1, p + 1))
                  }
                  disabled={gridPage >= gridPageCount - 1}
                  className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
                >
                  다음 ▶
                </button>
              </div>
            ) : null}
          </div>

          {effectiveSources.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 text-center dark:border-gray-600"
              style={{ minHeight: getGridBoxHeight(layout) }}
            >
              <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                등록된 카메라가 없습니다.
              </div>
              <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                관리자 → 카메라 관리에서 등록하면 여기에 올라옵니다.
              </div>
            </div>
          ) : (
          <div
            className={`grid ${gridColsClass} gap-3`}
            style={{ minHeight: getGridBoxHeight(layout) }}
          >
            {visibleSlots.map((slot) => {
              const badge = getStatusBadge(slot.source);

              return (
                <div
                  key={`slot-${slot.slotIndex}`}
                  onClick={() => handleSlotClick(slot.slotIndex)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDropOnSlot(e, slot.slotIndex)}
                  className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-[#0B1220] shadow-sm dark:border-gray-700"
                  style={{ minHeight: getSlotMinHeight(layout) }}
                >
                  <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#111827] px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="rounded-md bg-white/10 px-2 py-1 text-[10px] font-bold text-white">
                        CH {slot.channelNo}
                      </span>

                      {slot.source ? (
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold text-white">
                            {slot.source.name}
                          </div>
                          <div className="truncate text-[10px] text-white/60">
                            {getOwnerLine(slot.source) ||
                              getSourceTypeLabel(slot.source.sourceType)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-white/50">빈 슬롯</div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {slot.source ? (
                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-bold text-white ${badge.cls}`}
                        >
                          {badge.text}
                        </span>
                      ) : null}

                      {slot.source ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearSlot(slot.slotIndex);
                          }}
                          className="rounded-lg bg-white/10 px-2 py-1 text-[10px] font-semibold text-white hover:bg-white/15"
                        >
                          비우기
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="relative min-h-0 flex-1 bg-black">
                    <CameraPlayer source={slot.source} />
                  </div>
                </div>
              );
            })}
          </div>
          )}

        </div>
      </div>
    </div>
  );
}